import { createSlice, current } from '@reduxjs/toolkit';
import {
	getPaymentList,
	addNewPayment,
	setPaymentCancelled,
	updatePayment,
	deletePayment,
	getPayment,
	generatePayment,
	calculateModuleUsageFact,
	previewPayment,
} from '../payment/thunk';

export const initialState = {
	itemPaymentList: [],
	itemPaymentDetail: null,
	isPaymentLoading: false,
	isPaymentSuccess: false,

	paymentList: [],
	paymentListSelect: [],
	paymentListFiltered: [],
	isLoadingList: false,
	isAdding: false,
	isAdded: false,
	isAddError: false,
	isUpdating: false,
	isUpdated: false,
	isUpdateError: false,
	isDeleting: false,
	isDeleted: false,
	isDeleteError: false,
	requireCloseModal: false,
	paymentPreview: null,
	isCalculating: false,
	isCalculateError: false,
	isPreviewLoading: false,
	isPreviewError: false,
};

const PaymentSlice = createSlice({
	name: 'Payments',
	initialState,
	reducers: {
		setRequireCloseModal(state, action) {
			state.requireCloseModal = action.payload;
		},
		clearPaymentPreview(state) {
			state.paymentPreview = null;
			state.isCalculating = false;
			state.isCalculateError = false;
			state.isPreviewLoading = false;
			state.isPreviewError = false;
		},
	},
	extraReducers: builder => {
		builder.addCase(getPayment.fulfilled, (state, action) => {
			state.isPaymentLoading = false;
			state.isPaymentSuccess = true;
			state.itemPaymentDetail = action.payload;
			state.isLoadingList = false;
		});
		builder.addCase(getPayment.pending, state => {
			state.isPaymentLoading = true;
			state.isPaymentSuccess = null;
			state.error = null;
			state.isLoadingList = true;
		});
		builder.addCase(getPayment.rejected, (state, action) => {
			state.isPaymentLoading = false;
			state.isPaymentSuccess = false;
			state.error = action.error.message;
			state.isLoadingList = false;
		});

		builder.addCase(getPaymentList.pending, state => {
			state.isPaymentSuccess = true;
			state.error = null;
			state.isLoadingList = true;
		});
		builder.addCase(getPaymentList.fulfilled, (state, action) => {
			state.itemPaymentList = action.payload;
			state.paymentList = action.payload;
			state.paymentListFiltered =
				action.payload?.filter(
					item => item.payment_status?.code !== 'CND'
				) || [];
			state.isLoadingList = false;
			state.isPaymentSuccess = true;

			let paymentListSelect = [];
			action.payload.map(item =>
				paymentListSelect.push({
					item: item,
					value: item._id,
					label: item.name,
				})
			);

			state.paymentListSelect = paymentListSelect;
		});
		builder.addCase(getPaymentList.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isPaymentSuccess = true;
		});

		builder.addCase(addNewPayment.fulfilled, (state, action) => {
			state.itemPaymentList.push(action.payload);
			state.paymentList.push(action.payload);
			state.isLoadingList = false;
			state.isAdding = false;
			state.isAdded = true;
			state.isAddError = false;
			state.requireCloseModal = true;
		});
		builder.addCase(addNewPayment.pending, (state, action) => {
			state.isLoadingList = true;
			state.isAdding = true;
			state.isAdded = false;
			state.isAddError = false;
		});
		builder.addCase(addNewPayment.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isAdding = false;
			state.isAdded = false;
			state.isAddError = true;
		});

		builder.addCase(setPaymentCancelled.fulfilled, (state, action) => {
			state.itemPaymentList.push(action.payload);
			state.paymentList.push(action.payload);
			state.isPaymentSuccess = true;
			state.requireCloseModal = true;
			state.isLoadingList = false;
		});
		builder.addCase(setPaymentCancelled.pending, (state, action) => {
			state.isPaymentSuccess = null;
			state.isLoadingList = true;
		});
		builder.addCase(setPaymentCancelled.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isPaymentSuccess = false;
		});

		builder.addCase(updatePayment.fulfilled, (state, action) => {
			state.itemPaymentList = state.itemPaymentList.map(payment =>
				payment.paymentname === action.payload.old_payment
					? { ...payment, ...action.payload.updated_payment }
					: payment
			);
			state.paymentList = state.paymentList.map(payment =>
				payment._id.toString() === action.payload._id.toString()
					? { ...payment, ...action.payload }
					: payment
			);
			state.isLoadingList = false;
			state.isUpdating = false;
			state.isUpdated = true;
			state.isUpdateError = false;
			state.requireCloseModal = true;
		});
		builder.addCase(updatePayment.pending, state => {
			state.isLoadingList = true;
			state.isUpdating = true;
			state.isUpdated = false;
			state.isUpdateError = false;
		});
		builder.addCase(updatePayment.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isUpdating = false;
			state.isUpdated = false;
			state.isUpdateError = true;
		});

		builder.addCase(deletePayment.fulfilled, (state, action) => {
			state.itemPaymentList = current(state.itemPaymentList).filter(
				item => item._id !== action.payload._id
			);
			state.paymentList = current(state.paymentList).filter(
				item => item._id !== action.payload._id
			);
			state.isLoadingList = false;
			state.isDeleting = false;
			state.isDeleted = true;
			state.isDelete = false;
			state.requireCloseModal = true;
		});
		builder.addCase(deletePayment.pending, (state, action) => {
			state.isLoadingList = true;
			state.isDeleting = false;
			state.isDeleted = true;
			state.isDelete = false;
		});
		builder.addCase(deletePayment.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isDeleting = false;
			state.isDeleted = false;
			state.isDelete = true;
		});

		builder.addCase(generatePayment.fulfilled, (state, action) => {
			// Agregar los nuevos pagos generados a las listas existentes
			if (
				action.payload &&
				action.payload.payments &&
				Array.isArray(action.payload.payments)
			) {
				// Agregar al inicio de las listas para que aparezcan primero
				state.itemPaymentList.unshift(...action.payload.payments);
				state.paymentList.unshift(...action.payload.payments);

				// Actualizar paymentListSelect
				let paymentListSelect = [];
				state.paymentList.map(item =>
					paymentListSelect.push({
						item: item,
						value: item._id,
						label: item.name,
					})
				);
				state.paymentListSelect = paymentListSelect;
			}

			state.isLoadingList = false;
			state.isPaymentSuccess = true;
			state.requireCloseModal = true;
		});
		builder.addCase(generatePayment.pending, (state, action) => {
			state.isLoadingList = true;
			state.isPaymentSuccess = null;
		});
		builder.addCase(generatePayment.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isPaymentSuccess = false;
		});

		builder.addCase(calculateModuleUsageFact.pending, state => {
			state.isCalculating = true;
			state.isCalculateError = false;
			state.paymentPreview = null;
			state.isPreviewError = false;
		});
		builder.addCase(calculateModuleUsageFact.fulfilled, state => {
			state.isCalculating = false;
			state.isCalculateError = false;
		});
		builder.addCase(calculateModuleUsageFact.rejected, (state, action) => {
			state.isCalculating = false;
			state.isCalculateError = true;
			state.paymentPreview = null;
			state.error = action.payload || null;
		});

		builder.addCase(previewPayment.pending, state => {
			state.isPreviewLoading = true;
			state.isPreviewError = false;
			state.paymentPreview = null;
		});
		builder.addCase(previewPayment.fulfilled, (state, action) => {
			state.isPreviewLoading = false;
			state.isPreviewError = false;
			state.paymentPreview = action.payload;
		});
		builder.addCase(previewPayment.rejected, (state, action) => {
			state.isPreviewLoading = false;
			state.isPreviewError = true;
			state.paymentPreview = null;
			state.error = action.payload || null;
		});
	},
});

export const { setRequireCloseModal, clearPaymentPreview } = PaymentSlice.actions;

export default PaymentSlice.reducer;
